import React, { memo, useState } from 'react';
import { FiCalendar, FiExternalLink, FiTrash2, FiMinus, FiPlus, FiAlertCircle } from 'react-icons/fi';
import { formatDistanceToNow } from 'date-fns';

const formatDate = (dateString) => {
  if (!dateString) return '';
  return formatDistanceToNow(new Date(dateString), { addSuffix: true });
};

const GHOST_THRESHOLD_DAYS = 21;

const isGhosted = (application) => {
  if (application?.status !== 'applied') return false;
  const appliedAt = application.appliedAt;
  if (!appliedAt) return false;
  const ms = new Date(appliedAt).getTime();
  if (Number.isNaN(ms)) return false;
  return Date.now() - ms > GHOST_THRESHOLD_DAYS * 24 * 60 * 60 * 1000;
};

/**
 * Inline round counter for applications in `interviewing`. Allows the user
 * to bump or drop the round, with a server round-trip per change. The
 * counter cannot go below 1 once interviewing.
 */
const RoundCounter = ({ application }) => {
  const initial = Math.max(1, parseInt(application.round, 10) || 1);
  const [round, setRound] = useState(initial);
  const [busy, setBusy] = useState(false);

  const update = async (next) => {
    if (next < 1 || next === round || busy) return;
    setBusy(true);
    const prev = round;
    setRound(next);
    try {
      if (typeof application.onRoundChange === 'function') {
        await application.onRoundChange(application, next);
      }
    } catch (err) {
      setRound(prev);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="flex items-center gap-1 select-none"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      role="group"
      aria-label="Interview round"
      title="Interview round (≥ 1). Rejection at round 2+ counts as a late rejection."
    >
      <button
        type="button"
        disabled={busy || round <= 1}
        onClick={() => update(round - 1)}
        className="h-5 w-5 inline-flex items-center justify-center rounded border border-yellow-300 bg-white text-yellow-700 hover:bg-yellow-50 disabled:opacity-40 disabled:cursor-not-allowed"
        aria-label="Decrease round"
      >
        <FiMinus className="h-3 w-3" />
      </button>
      <span className="px-1.5 text-[11px] font-semibold text-yellow-800 min-w-[3.25rem] text-center">
        Round {round}
      </span>
      <button
        type="button"
        disabled={busy}
        onClick={() => update(round + 1)}
        className="h-5 w-5 inline-flex items-center justify-center rounded border border-yellow-300 bg-white text-yellow-700 hover:bg-yellow-50 disabled:opacity-40 disabled:cursor-not-allowed"
        aria-label="Increase round"
      >
        <FiPlus className="h-3 w-3" />
      </button>
    </div>
  );
};

const ApplicationCard = memo(({ application, refreshApplications, isHighlighted = false }) => {
  const jobTitle = application.jobTitle || 'application';
  const company = application.company || '';
  const ghosted = isGhosted(application);
  const showRoundCounter = application.status === 'interviewing';

  return (
    <div
      className={`bg-white p-4 rounded-xl border shadow-sm ${
        isHighlighted
          ? 'border-primary-500 ring-2 ring-primary-300 shadow-md'
          : ghosted
            ? 'border-amber-300'
            : 'border-gray-200'
      } hover:shadow-md transition-all duration-200`}
    >
      <div className="flex justify-between">
        <h3 className="font-medium text-gray-900 truncate" title={jobTitle}>
          {application.jobTitle}
        </h3>
        <div className="flex space-x-1">
          <button
            onClick={(e) => {
              e.stopPropagation();
              application.onView(application);
            }}
            className="text-gray-400 hover:text-primary-600 p-1 rounded-full hover:bg-gray-100"
            title="View Details"
            aria-label={`View details for ${jobTitle}`}
          >
            <FiExternalLink className="h-4 w-4" aria-hidden="true" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              application.onDelete(application);
            }}
            className="text-gray-400 hover:text-red-600 p-1 rounded-full hover:bg-gray-100"
            title="Delete Application"
            aria-label={`Delete application for ${jobTitle}`}
          >
            <FiTrash2 className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      </div>

      <p className="text-sm text-gray-500 truncate mt-1" title={company}>
        {application.company}
      </p>

      {ghosted && (
        <div
          className="mt-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-[10px] font-medium text-amber-800"
          title={`No update for over ${GHOST_THRESHOLD_DAYS} days`}
        >
          <FiAlertCircle className="h-3 w-3" />
          Ghosted
        </div>
      )}

      <div className="mt-3 flex items-center justify-between gap-2">
        <div className="flex items-center text-xs text-gray-500 min-w-0">
          <FiCalendar className="mr-1 h-3 w-3 flex-shrink-0" aria-hidden="true" />
          <span className="truncate">{formatDate(application.appliedAt)}</span>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {showRoundCounter && <RoundCounter application={application} />}
          <button
            onClick={(e) => {
              e.stopPropagation();
              application.onView(application);
            }}
            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
              isHighlighted
                ? 'bg-primary-200 text-primary-800'
                : 'bg-primary-100 text-primary-800'
            }`}
            aria-label={`View details for ${jobTitle}`}
          >
            <FiExternalLink className="mr-1 h-3 w-3" aria-hidden="true" />
            View
          </button>
        </div>
      </div>
    </div>
  );
});

export default ApplicationCard;
